import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

const BG = "#FDFCF8";
const GOLD_TEXT = {
  backgroundImage: "linear-gradient(180deg, #D9B26A 0%, #A9772F 45%, #7A5422 70%, #C9A060 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

function Brand() {
  return (
    <div data-testid="confirmed-brand" className="text-center">
      <img
        src="/images/fk-symbol.webp"
        alt=""
        aria-hidden="true"
        width="696"
        height="238"
        className="mx-auto w-[300px] sm:w-[420px] md:w-[520px] h-auto"
        style={{
          WebkitMaskImage: "radial-gradient(ellipse 50% 50% at 50% 50%, #000 70%, transparent 100%)",
          maskImage: "radial-gradient(ellipse 50% 50% at 50% 50%, #000 70%, transparent 100%)",
        }}
        fetchpriority="high"
      />
      <p
        className="-mt-1 font-serif-display font-medium uppercase text-[40px] sm:text-[52px] md:text-[60px] leading-[1.05]"
        style={{ letterSpacing: "0.03em" }}
      >
        <span style={GOLD_TEXT}>Forrás-Kód</span>
        <sup className="text-[0.3em] ml-1 text-[#A9772F]" style={{ verticalAlign: "1.25em" }}>®</sup>
      </p>
      <div className="mt-3 flex items-center justify-center gap-4">
        <span aria-hidden="true" className="h-px w-16 sm:w-28 md:w-32 bg-[linear-gradient(90deg,transparent,#C9A060)]" />
        <span className="font-serif-display uppercase text-[15px] md:text-[19px] text-[#5E4524]" style={{ letterSpacing: "0.45em", paddingLeft: "0.45em" }}>
          Kísérő
        </span>
        <span aria-hidden="true" className="h-px w-16 sm:w-28 md:w-32 bg-[linear-gradient(90deg,#C9A060,transparent)]" />
      </div>
      <p className="mt-2 font-serif-display uppercase text-[12px] md:text-[15px] text-[#5E4524]" style={{ letterSpacing: "0.42em", paddingLeft: "0.42em" }}>
        Szász-Tóth Enikő
      </p>
    </div>
  );
}

const FADE_L = "linear-gradient(90deg, #000 70%, transparent 100%), linear-gradient(0deg, #000 70%, transparent 100%)";
const FADE_R = "linear-gradient(270deg, #000 70%, transparent 100%), linear-gradient(0deg, #000 70%, transparent 100%)";

function Waves() {
  const common = "pointer-events-none select-none absolute bottom-0 w-[46vw] max-w-[520px] min-w-[220px] h-auto";
  return (
    <>
      <img src="/images/fk-wave-left.webp" alt="" aria-hidden="true" loading="lazy" className={`${common} left-0`} style={{ WebkitMaskImage: FADE_L, maskImage: FADE_L, WebkitMaskComposite: "source-in", maskComposite: "intersect" }} />
      <img src="/images/fk-wave-right.webp" alt="" aria-hidden="true" loading="lazy" className={`${common} right-0`} style={{ WebkitMaskImage: FADE_R, maskImage: FADE_R, WebkitMaskComposite: "source-in", maskComposite: "intersect" }} />
    </>
  );
}

export default function NewsletterConfirmed() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Köszönjük a megerősítést! | Forrás-Kód® Kísérő – Szász-Tóth Enikő";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  return (
    <main
      data-testid="newsletter-confirmed-page"
      className="relative min-h-screen overflow-hidden flex items-center justify-center px-6 py-12 md:py-16"
      style={{
        backgroundColor: BG,
        backgroundImage:
          "radial-gradient(ellipse 55% 40% at 50% 18%, rgba(240,214,160,0.28), rgba(253,252,248,0) 70%), radial-gradient(ellipse 50% 35% at 50% 100%, rgba(240,220,180,0.25), rgba(253,252,248,0) 70%)",
      }}
    >
      <Waves />
      <div className="relative z-10 w-full max-w-2xl text-center">
        <Brand />

        <div
          aria-hidden="true"
          className="mx-auto mt-10 md:mt-12 w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center"
          style={{
            background: "radial-gradient(circle at 50% 40%, #F7EEDD, #EEE2CC)",
            boxShadow: "0 0 0 10px rgba(240,226,200,0.35), 0 18px 40px -18px rgba(160,122,40,0.45)",
          }}
        >
          <Check size={38} strokeWidth={2.6} className="text-[#6B4F2A]" />
        </div>

        <h1
          data-testid="newsletter-confirmed-title"
          className="mt-8 md:mt-10 font-serif-display font-medium text-[#5E4524] text-[36px] sm:text-[46px] md:text-[56px] leading-[1.1]"
        >
          Köszönjük a megerősítést!
        </h1>

        <div aria-hidden="true" className="mt-6 flex items-center justify-center gap-3">
          <span className="h-px w-24 sm:w-40 md:w-56 bg-[linear-gradient(90deg,transparent,#D4B47A)]" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#C9A060]" />
          <span className="h-px w-24 sm:w-40 md:w-56 bg-[linear-gradient(90deg,#D4B47A,transparent)]" />
        </div>

        <div data-testid="newsletter-confirmed-text" className="mt-6 font-body text-[#5A4A36] text-[16px] md:text-[19px] leading-[1.75]">
          <p>Sikeresen megerősítetted a feliratkozásodat!</p>
          <p className="mt-2 max-w-xl mx-auto">
            Hamarosan érkeznek hozzád a Forrás-Kód<sup className="text-[0.6em]">®</sup> újdonságai,
            programjai és inspiráló gondolatok.
          </p>
        </div>

        <Link
          to="/"
          data-testid="newsletter-confirmed-home"
          className="group mt-10 md:mt-12 inline-flex items-center justify-center gap-4 rounded-[6px] px-10 md:px-16 py-4 md:py-[18px] font-body text-[16px] md:text-[18px] text-white tracking-[0.02em] transition-[transform,box-shadow,filter] duration-300 hover:-translate-y-0.5 hover:brightness-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#A9772F]"
          style={{
            background: "linear-gradient(180deg, #D2B27A 0%, #B48C4E 55%, #A47C40 100%)",
            boxShadow: "0 14px 30px -14px rgba(140,100,40,0.55), inset 0 1px 0 rgba(255,255,255,0.35)",
          }}
        >
          Vissza a weboldalra
          <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </Link>
      </div>
    </main>
  );
}
